// Helpers de roles del frontend.
// Mantenemos compatibilidad con roles legacy.
export const ROLES = {
  admin: 'Admin',
  direccion: 'Dirección',
  administrativo: 'Administrativo',
  doctor: 'Doctor',
  enfermeria: 'Enfermería',
  anestesista: 'Anestesista',
  tablet: 'Tablet LOPD (kiosco)',
  tablet_consent: 'Tablet Consentimientos (kiosco)',
};

const LEGACY_MAP = {
  admin: 'admin',
  direccion: 'direccion',
  administrativo: 'administrativo',
  doctor: 'doctor',  // Phase 27 — sistema de citas (agenda propia)
  enfermeria: 'enfermeria',  // Phase 27.5 — ve todas las agendas, sin notas internas
  anestesista: 'anestesista',  // 2026-07-24 — solo quirófano y anestesia
  tablet: 'tablet',  // Phase 24 — kiosk iPad LOPD
  tablet_consent: 'tablet_consent',  // Phase 23.11 — kiosk iPad firmas
  medico: 'direccion',
  secretaria: 'direccion',
  recepcion: 'administrativo',
};

export function normalizeRole(role) {
  return LEGACY_MAP[(role || '').toLowerCase()] || 'administrativo';
}

export function isManagement(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin' || r === 'direccion';
}

export function isAdmin(user) {
  return normalizeRole(user?.role) === 'admin';
}

/**
 * Roles que pueden AJUSTAR el hueco (forzar solapes o fuera de horario), poner
 * una hora manual y modificar los tiempos de la cita. Incluye a todo el
 * personal clínico: admin, dirección, administrativo, enfermería y también los
 * doctores (2026-07-16: todos deben poder poner/editar la hora manualmente).
 */
export function canForceSlot(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin' || r === 'direccion' || r === 'administrativo' || r === 'enfermeria' || r === 'doctor';
}

/**
 * Phase 27 — Sistema de citas.
 * Si el usuario es `doctor`, solo ve/gestiona su propia agenda
 * (user.agenda_id, que llega enriquecido desde /api/auth/me).
 */
/** Perfiles CLÍNICOS: doctor y anestesista. Es la misma raya que traza el
 *  servidor con los datos económicos (`_staff_cobros`, 2026-08-26): no crean,
 *  no editan y no borran cobros. Tenerla con nombre evita que la pantalla y el
 *  servidor midan distinto — que es justo lo que pasaba: la pestaña de Cobros
 *  se le escondía al doctor y no al anestesista, y a este le salía un botón
 *  que el servidor le iba a rechazar (2026-09-17). */
export function isClinico(user) {
  const r = normalizeRole(user?.role);
  return r === 'doctor' || r === 'anestesista';
}

export function isDoctor(user) {
  return normalizeRole(user?.role) === 'doctor';
}

/**
 * Notas de administración del paciente (pagos, gestiones, recordatorios…).
 * SOLO administración y dirección: admin, dirección, administrativo. NI
 * enfermería NI médicos; admin las ve por ser admin (2026-07-13).
 * El backend lo vuelve a comprobar en /api/patients/{id}/notas-internas.
 */
/**
 * Administración: admin, dirección y administrativas.
 * Es quien puede ver PRESUPUESTOS — el importe es confidencial y no lo ven ni
 * enfermería, ni anestesistas, ni doctores (2026-08-18). El servidor
 * lo comprueba igual con `require_gestion()`: esto es solo para no enseñar una
 * puerta que va a dar 403.
 */
export function isGestion(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin' || r === 'direccion' || r === 'administrativo';
}

/**
 * Quién habla con el Agente IA (D7, 22/9/2026: «los doctores no;
 * solo el escribir con IA / ajustar la historia; pero no pregunta libre al
 * agente: eso solo administrativos, administradores y enfermería»).
 *
 * Es la MISMA lista que `auth.AGENTE_ROLES` del servidor, que es quien pone
 * la regla (doctor y anestesista reciben 403). Aquí solo sirve para no
 * ofrecerles la campana, el buscador del Agente ni el cuadro de la agenda.
 * Vive en lib/roles para que todas las pantallas midan igual: antes estaba
 * copiada en AppShell y la agenda (CitasPage) no la miraba.
 *
 * Se compara el rol CRUDO con los viejos escritos a mano, como hace el
 * servidor: con `normalizeRole`, un rol vacío o desconocido caería en
 * «administrativo» (su valor por defecto) y tendría Agente.
 */
const ROLES_CON_AGENTE = [
  'admin', 'direccion', 'administrativo', 'enfermeria',
  'medico', 'secretaria', 'recepcion',  // viejos: dirección / administrativo
];

export function tieneAgente(user) {
  return ROLES_CON_AGENTE.includes((user?.role || '').toLowerCase());
}

export function canSeeInternalNotes(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin' || r === 'direccion' || r === 'administrativo';
}

/**
 * Phase 24 — Modo kiosko (iPad mostrador LOPD).
 * Si el usuario es `tablet`, la app:
 *   - oculta sidebar y topbar
 *   - fuerza redirect permanente a /firma-lopd
 *   - bloquea la navegación a cualquier otra ruta
 *   - oculta el logout en la pantalla LOPD
 */
export function isTablet(user) {
  return normalizeRole(user?.role) === 'tablet';
}

/**
 * Phase 23.11 — Modo kiosko (iPad consentimientos).
 * Si el usuario es `tablet_consent`, la app:
 *   - oculta sidebar y topbar
 *   - fuerza redirect permanente a /firma-consent
 *   - bloquea la navegación a cualquier otra ruta
 */
export function isTabletConsent(user) {
  return normalizeRole(user?.role) === 'tablet_consent';
}


export function roleLabel(role) {
  return ROLES[normalizeRole(role)] || role || '—';
}

/** 2026-07-24 — Anestesistas: solo ven Quirófano y Anestesia. */
export function isAnestesista(user) {
  return normalizeRole(user?.role) === 'anestesista';
}

/**
 * Quién lleva el catálogo de medicamentos favoritos: añadir, cambiar y
 * borrar plantillas (M34, informe del 22/9/2026). Los sanitarios y
 * dirección; recepción las lee para hacer una receta, pero no las toca.
 *
 * Es la MISMA lista que `recipes_router._CATALOGO_MEDICAMENTOS`, que es quien
 * pone la regla. Aquí solo sirve para no enseñar «Añadir favorito», «Editar»
 * y la papelera a quien el servidor le va a contestar 403 (segunda pasada,
 * 23/9/2026: el servidor se cerró y la pantalla seguía ofreciéndolo).
 *
 * Rol CRUDO, sin `normalizeRole`, porque así compara `require_role` en el
 * servidor: las dos listas son literalmente la misma, roles viejos incluidos.
 */
const ROLES_CATALOGO_MEDICAMENTOS = [
  'admin', 'direccion', 'medico', 'secretaria',  // dirección y sus roles viejos
  'doctor', 'anestesista', 'enfermeria',          // sanitarios
];

export function llevaCatalogoMedicamentos(user) {
  return ROLES_CATALOGO_MEDICAMENTOS.includes(user?.role || '');
}
